import { Loader } from '@googlemaps/js-api-loader'
import {
  InjectionKey,
  Ref,
  computed,
  inject,
  onMounted,
  provide,
  ref,
} from 'vue'

interface GoogleData {
  api: typeof google | undefined
  map: google.maps.Map | undefined
  loaded: Ref<boolean>
  zoom: Ref<number>
}

export const googleDataKey = Symbol() as InjectionKey<GoogleData>

export function provideGoogleMapsData(
  apiKey: string,
  mapId: string,
  mapdiv: Ref<HTMLDivElement | undefined>,
  mapOptions: google.maps.MapOptions = {},
) {
  const googleData: GoogleData = {
    api: undefined,
    map: undefined,
    loaded: ref(false),
    zoom: ref(8),
  }

  provide(googleDataKey, googleData)

  const loader = new Loader({
    apiKey,
    libraries: ['places', 'marker'],
  })

  const defaultOptions = {
    center: { lat: 45.81938072674977, lng: -122.66331674781095 },
    zoom: 9,
  }

  function initializeMap() {
    if (!googleData.api) {
      return console.error('Missing Google')
    }
    if (!mapdiv.value) {
      return console.error('Missing mapdiv')
    }
    const map = new googleData.api.maps.Map(mapdiv.value, {
      ...defaultOptions,
      ...mapOptions,
      mapId,
    })
    map.addListener('zoom_changed', () => {
      googleData.zoom.value = map.getZoom() || 8
    })
    googleData.map = map
    setTimeout(() => {
      googleData.loaded.value = true
    }, 10)
  }

  onMounted(() => {
    // Check if we're offline or have poor connectivity
    const isOffline =
      !navigator.onLine ||
      (window.location.protocol === 'https:' && !window.navigator.serviceWorker)

    if (isOffline) {
      console.warn(
        'Device appears to be offline, skipping Google Maps initialization',
      )
      googleData.loaded.value = true
      return
    }

    // Add timeout to prevent hanging on poor network connections
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Google Maps API load timeout')), 10000) // 10 second timeout
    })

    Promise.race([loader.load(), timeoutPromise])
      .then(() => {
        googleData.api = window.google
        initializeMap()
      })
      .catch((error) => {
        console.warn('Google Maps API failed to load:', error)
        // Set loaded to true anyway to prevent UI blocking
        // The app should work without maps in offline mode
        googleData.loaded.value = true
      })
  })

  return googleData
}

export function useProvidedGoogleMapsData() {
  const googleData = inject(googleDataKey) || {
    api: undefined,
    map: undefined,
    loaded: ref(false),
    zoom: ref(8),
  }

  const loaded = computed(() => {
    return googleData.loaded.value
  })

  const zoom = computed(() => {
    return googleData.zoom.value
  })

  return {
    googleData,
    api: googleData.api,
    map: googleData.map,
    loaded,
    zoom,
  }
}
